// ─── Card system and base primitives ─────────────────────────────────────────
//
// "The base visual unit across the platform. Soft tone shifts, gentle inner
// light, very soft shadow, or a thin pale stroke separate cards from each
// other — **never black outlines.**"
//
// Everything here styles from the tokens in theme.css. No component in this
// directory contains a literal colour; switching `data-nl-theme` re-skins the
// lot, which is the requirement the theming section actually asks for.

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import type {
  CSSProperties, ReactNode,
  PointerEvent as ReactPointerEvent,
  MouseEvent as ReactMouseEvent,
  KeyboardEvent as ReactKeyboardEvent,
} from "react";

/* The one change to this block, and it is plumbing rather than art: Farndale
 * reads these from its own `client/public/tiles/`, which means an app that
 * consumes the chassis has to copy nine PNGs into the right place or every
 * tile renders bare. That is precisely the class of "configuration step an app
 * has to get right" the chassis exists to delete, so the files travel WITH the
 * package and the bundler resolves them. Byte-identical to Farndale's own —
 * see `proof/farndale-source.txt` for their checksums. */
import peopleArt from "./assets/tiles/people.png";
import tasksArt from "./assets/tiles/tasks.png";
import mapArt from "./assets/tiles/map.png";
import entriesArt from "./assets/tiles/entries.png";
import scheduleArt from "./assets/tiles/schedule.png";
import resultsArt from "./assets/tiles/results.png";
import dutiesArt from "./assets/tiles/duties.png";
import setupArt from "./assets/tiles/setup.png";
import generalArt from "./assets/tiles/general.png";

export type WatermarkMotif =
  | "stewards" | "map" | "entries" | "schedule" | "results" | "assets"
  | "tasks" | "duties" | "setup" | "general" | "none";


/**
 * Sol's tile artwork (approved by Drea, 2026-08-18), replacing the geometric
 * shapes below on every tile that has one.
 *
 * They are extracted from the reference sheets in `docs/mockups/` as WHITE
 * linework on transparent, not as the flattened green pictures they arrived
 * as. That matters: the tile colour has already changed three times, and a
 * baked-in background would have to be re-cut every time it changes again.
 * Tinted by `color` at low opacity here, they follow the theme instead.
 *
 * Anything without an entry falls through to the geometric watermark, which
 * is why `assets` still works and why a new motif cannot render as nothing.
 */
const ARTWORK: Partial<Record<WatermarkMotif, string>> = {
  stewards: peopleArt,
  tasks: tasksArt,
  map: mapArt,
  entries: entriesArt,
  schedule: scheduleArt,
  results: resultsArt,
  duties: dutiesArt,
  setup: setupArt,
  general: generalArt,
};

/**
 * The faint subject motif a card can carry.
 *
 * "Cards can carry a faint, low-contrast 'watermark' motif specific to their
 * subject … embossing-style, not illustration. The goal: stop the interface
 * feeling like a corporate dashboard without tipping into a 'kids' app' look."
 *
 * So these are deliberately geometric and nearly invisible — shapes suggesting
 * the subject, at very low opacity, not pictures of it.
 */
function Watermark({ motif }: { motif: WatermarkMotif }) {
  if (motif === "none") return null;

  const artwork = ARTWORK[motif];
  if (artwork) {
    return (
      <img
        src={artwork} alt="" aria-hidden="true" draggable={false} loading="lazy"
        className="pointer-events-none absolute right-[-8px] bottom-[-10px] h-[78%] w-auto max-w-[86%] object-contain object-right-bottom"
        /* 0.35 (Drea, 2026-08-19, after seeing it on a real phone).
         *
         * It was 0.13, derived from the reference sheet: its STRONGEST strokes
         * sit ~11% of the way from the tile colour to white, and the alpha
         * here is normalised so the strongest stroke is opaque, so 0.13
         * reproduced the sheet faithfully. Faithfully, and too faintly — the
         * sheet is viewed as a big flat image, while the real tile is 172px
         * wide on a phone held in daylight, and most of a drawing's pixels are
         * antialiased edges well below that peak. Reproducing the reference
         * exactly was the wrong target.
         *
         * Checked as a ladder against the live artwork at true tile width
         * before choosing: 0.13 near-invisible, 0.28 clearly legible, 0.38
         * starting to compete with the tile's own label. */
        style={{ opacity: 0.35 }}
      />
    );
  }

  const common = {
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.25,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 120 120"
      className="pointer-events-none absolute right-[-8px] bottom-[-10px] h-[78%] w-auto"
      style={{ color: "var(--nl-ink)", opacity: 0.05 }}
    >
      {motif === "stewards" && (
        <g {...common}>
          <circle cx="42" cy="34" r="13" /><path d="M20 88c0-14 10-24 22-24s22 10 22 24" />
          <circle cx="80" cy="42" r="10" /><path d="M64 88c0-11 7-19 16-19s16 8 16 19" />
          <path d="M42 47v18M36 52h12" />
        </g>
      )}
      {motif === "map" && (
        <g {...common}>
          <path d="M8 92c22-10 30 6 52-4s30 4 52-8" /><path d="M4 70c22-10 34 6 56-4s28 4 56-8" />
          <path d="M12 110c20-8 32 4 52-4s32 2 54-8" />
          <path d="M60 30l10 18H50z" /><circle cx="60" cy="24" r="5" />
        </g>
      )}
      {motif === "entries" && (
        <g {...common}>
          <rect x="22" y="18" width="62" height="80" rx="6" />
          <path d="M34 40h38M34 54h38M34 68h24" />
          <path d="M84 82l12 12-12 4z" />
        </g>
      )}
      {motif === "schedule" && (
        <g {...common}>
          <circle cx="58" cy="58" r="34" /><path d="M58 36v24l16 9" />
          <path d="M58 8v8M58 100v8M8 58h8M100 58h8" />
        </g>
      )}
      {motif === "results" && (
        <g {...common}>
          <circle cx="58" cy="42" r="18" /><path d="M46 58l-8 32 20-12 20 12-8-32" />
          <path d="M58 34v16M50 42h16" />
        </g>
      )}
      {motif === "assets" && (
        <g {...common}>
          <path d="M14 78l30-34 30 34z" /><path d="M14 78h60" />
          <rect x="72" y="58" width="34" height="20" rx="4" /><circle cx="82" cy="82" r="5" /><circle cx="98" cy="82" r="5" />
        </g>
      )}
    </svg>
  );
}

export type CardProps = {
  children: ReactNode;
  /**
   * `light` puts the card on the same pale surface as the Ask Mira bar, with
   * dark text (Drea, 2026-08-16) — used by the action-tile pairs.
   *
   * `module` is the Home grid (Drea, 2026-08-17): light overcorrected and the
   * original was too dark, so this is a step up from `--nl-tile` with a
   * distinctly brighter border. The eye is drawn by EDGE CONTRAST rather than
   * by a brighter block, which is what keeps the grid calm at arm's length in
   * daylight.
   */
  /** `info`: a summary/info card, MID GREEN by standing rule (Drea,
   *  2026-10-05). See --nl-info-card in theme.css. */
  tone?: "dark" | "light" | "module" | "info";
  motif?: WatermarkMotif;
  /** Optional image treatment — tiles must be architecturally capable of this,
   *  not only a solid colour. */
  backgroundImage?: string;
  onPress?: () => void;
  /**
   * A destination, which makes the whole card a real anchor.
   *
   * Behaviour Memorial Hall's tiles already had and would otherwise silently
   * lose on adopting the chassis: middle-click and open-in-new-tab. Farndale
   * passes no `href`, so its cards stay the `div` they are today and the
   * extraction stays pixel-exact — verified, not assumed, by the Stage 1 gate.
   */
  href?: string;
  className?: string;
  style?: CSSProperties;
  ariaLabel?: string;
  /** `aria-disabled` for a tile with nothing behind it. An attribute, not a
   *  style — nothing about the card's appearance changes. */
  ariaDisabled?: boolean;
  /**
   * This is the one currently open.
   *
   * An ATTRIBUTE, deliberately: a tile that is only marked by being a
   * different colour is not marked at all for somebody who cannot see the
   * difference, and this library says so about its own status dots. Added
   * 2026-09-26 when the Documents category tiles moved onto this component
   * and would otherwise have lost the `aria-current` the old ones carried.
   */
  ariaCurrent?: boolean;
  /** A hook for the visual-regression harness and the app suites. Attributes
   *  do not render. */
  testId?: string;
};

/** The base card. Separation comes from a thin pale stroke plus soft shadow
 *  and a gentle inner light — never a hard outline. */
export function Card({
  children, motif = "none", backgroundImage, onPress, href, className = "", style, ariaLabel,
  tone = "dark", ariaDisabled, ariaCurrent, testId,
}: CardProps) {
  // An anchor when it has a destination, a div with a button role when it only
  // has a handler. Never both: `role="button"` on an <a> tells a screen reader
  // it is a button and then behaves like a link.
  const Tag = href ? "a" : "div";
  const interactive = !!onPress;
  return (
    <Tag
      {...(href ? { href } : {})}
      role={!href && interactive ? "button" : undefined}
      tabIndex={!href && interactive ? 0 : undefined}
      aria-label={ariaLabel}
      aria-disabled={ariaDisabled || undefined}
      aria-current={ariaCurrent || undefined}
      data-testid={testId}
      onClick={onPress ? (e: ReactMouseEvent) => {
        // A modified click is the person asking the BROWSER for this link.
        // Intercepting it is how an app breaks middle-click unnoticed.
        if (href && (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0)) return;
        if (href) e.preventDefault();
        onPress();
      } : undefined}
      onKeyDown={!href && interactive ? (e: ReactKeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onPress!(); }
      } : undefined}
      className={[
        "relative overflow-hidden transition-colors",
        interactive || href ? "cursor-pointer" : "",
        backgroundImage ? "nl-tile-image" : "",
        tone === "light" ? "nl-light-card" : "",
        className,
      ].join(" ")}
      style={{
        borderRadius: "var(--nl-radius)",
        background: backgroundImage ? undefined
          : tone === "light" ? "var(--nl-band)"
          : tone === "module" ? "var(--nl-module-tile)"
          : tone === "info" ? "var(--nl-info-card)"
          : "var(--nl-tile)",
        backgroundImage: backgroundImage ? `url(${backgroundImage})` : undefined,
        border: tone === "module"
          ? "1px solid var(--nl-module-tile-stroke)"
          : tone === "info" ? "1px solid var(--nl-info-card-stroke)"
          : "1px solid var(--nl-stroke)",
        boxShadow: "var(--nl-shadow), var(--nl-inner-light)",
        color: tone === "info" ? "var(--nl-info-card-ink)" : "var(--nl-ink)",
        // An info card re-points the ink tokens for everything inside it, so
        // a faint label written for dark green stays readable on mid green.
        ...(tone === "info" ? {
          "--nl-ink": "var(--nl-info-card-ink)",
          "--nl-ink-dim": "var(--nl-info-card-ink-dim)",
          "--nl-ink-faint": "var(--nl-info-card-ink-dim)",
        } as React.CSSProperties : {}),
        // The card IS the link, so the default underline would run under a
        // whole tile.
        textDecoration: href ? "none" : undefined,
        ...style,
      }}
    >
      <Watermark motif={motif} />
      <div className="relative">{children}</div>
    </Tag>
  );
}

/**
 * A tile label that shrinks to fit rather than being cut off.
 *
 * ── The decision, and why it is a chassis rule ──────────────────────────────
 *
 * Drea, 2026-09-24. The tile box stays exactly the size it is, the label keeps
 * every character it has — no truncation, no shorter words — and the TYPE gets
 * smaller, but only on a label that would otherwise touch or cross the edge.
 *
 * It lives here rather than in any one app because a label too long for a tile
 * is not a Memorial Hall problem: Farndale has one too. `SCHEDULE` is 133.17px
 * inside a 131px box and has been clipped by 2.17px in the live show app all
 * along. This shrinks it by about 2%, which is visible if you measure it and
 * invisible if you do not, and that is the accepted outcome rather than a side
 * effect to be engineered around.
 *
 * ── Why measured rather than a character count ──────────────────────────────
 *
 * The obvious implementation is "more than N characters, go smaller", and it
 * is wrong in both directions: `MAP` and `MEMBERS` differ by four characters
 * and 68 pixels, and the same count in different letters fits or does not. So
 * this asks the browser what the text actually measures, which is the only
 * thing that answers the question being asked.
 *
 * Wrapping is left alone on purpose. `MINUTES CAPTURE` breaks over two lines
 * and clips nothing, so by the rule as stated it needs no shrinking — the rule
 * is about touching the edges, and a wrapped label does not. Only a run that
 * cannot break, and so overflows sideways, is scaled.
 */
function TileLabel({ label, dim }: { label: string; dim: boolean }) {
  const ref = useRef<HTMLDivElement | null>(null);
  /** `null` means "full size". It is the initial state, the state for every
   *  label that fits, and what a renderer with no layout keeps forever. */
  const [size, setSize] = useState<number | null>(null);
  /** State, readable from inside the measurement without making it a
   *  dependency — a callback that changes identity on every fit would
   *  re-subscribe the observer on every fit. */
  const sizeRef = useRef<number | null>(null);
  sizeRef.current = size;
  /** The unshrunk size, learned from the first measurement taken at it, so the
   *  type scale stays `text-2xl`'s business and is not duplicated here. */
  const baseRef = useRef<number | null>(null);

  const fit = useCallback(() => {
    const el = ref.current;
    if (!el || typeof document === "undefined") return;
    const box = el.clientWidth;
    if (box <= 0) return;

    const current = parseFloat(getComputedStyle(el).fontSize);
    if (!current) return;
    if (sizeRef.current === null) baseRef.current = current;
    const base = baseRef.current ?? current;

    // A Range, not `scrollWidth`. `scrollWidth` is an integer, so it cannot
    // see a label 0.4px over its box, and it reports nothing at all once the
    // label has been shrunk to fit — which would make re-growing impossible
    // when the viewport gets wider.
    //
    // An earlier version reset `el.style.fontSize` before measuring instead.
    // That read correctly and then broke on the write: clearing the inline
    // style is a direct DOM mutation React does not know about, so when the
    // recomputed size came out identical to the state already held, React
    // skipped the render and never put the style back. The label sat at full
    // size, clipped, with the correct answer in state.
    const range = document.createRange();
    range.selectNodeContents(el);
    const ink = range.getBoundingClientRect().width;
    if (ink <= 0) return;

    // What it would measure at full size — width is linear in font size, and
    // `tracking-wide` is 0.025em so the letter-spacing scales with it.
    const atBase = ink * (base / current);

    if (atBase <= box) { setSize(null); return; }

    // Floored to a hundredth so the value is identical run to run rather than
    // carrying float noise into a pixel diff, and shaved by a hair so rounding
    // cannot land back on the edge.
    const fitted = Math.floor(base * (box / atBase) * 100) / 100 - 0.01;
    // A floor, because a label nobody can read is not a fix. Nothing in either
    // app reaches it — CONTRACTORS, the longest unbreakable label, lands near
    // 16.6px.
    setSize(Math.max(12, fitted));
  }, []);

  useLayoutEffect(() => {
    fit();
    if (typeof window === "undefined" || typeof ResizeObserver === "undefined") return;
    // The PARENT is observed, not the label. The label's own height changes
    // every time this shrinks it, so observing it would feed the measurement
    // back into itself; the parent's width is what actually decides the fit
    // and does not move when the type does.
    const parent = ref.current?.parentElement;
    if (!parent) return;
    const ro = new ResizeObserver(() => fit());
    ro.observe(parent);
    return () => ro.disconnect();
  }, [fit, label]);

  useEffect(() => {
    // Measured against the WRONG font otherwise. The webfont arrives after
    // first paint and every metric changes when it does, so a ratio computed
    // against the fallback leaves the label either still clipped or needlessly
    // small.
    if (typeof document === "undefined" || !document.fonts) return;
    let live = true;
    void document.fonts.ready.then(() => { if (live) fit(); });
    return () => { live = false; };
  }, [fit]);

  return (
    <div
      ref={ref}
      className="nl-tile__label text-2xl tracking-wide"
      /* Dim ink is this design system's OWN way of saying "not live" — see
         DataState, where every not-available state is `--nl-ink-dim`. */
      style={{
        color: dim ? "var(--nl-ink-dim)" : "var(--nl-ink)",
        ...(size === null ? {} : { fontSize: `${size}px` }),
      }}
    >{label}</div>
  );
}

export type TileMetric = {
  label: string;
  value?: number | string;
  /** Red, not the neutral ink -- for a metric that means "you need to do
   *  something," not just "here is a count." Drea, 2026-09-27: a red badge
   *  on the Documents tile for anything due/overdue. Sparingly -- most
   *  metrics are informational and should stay neutral. */
  attention?: boolean;
};

/**
 * A Home-style navigation tile: a label and zero to three live metrics.
 *
 * "**A tile or strip only gets a live number when that number would change
 * what someone does next.** Don't invent a third metric to fill a row of two
 * just because other rows have three. A deliberately asymmetric display … is
 * correct, not unfinished." So `metrics` is genuinely allowed to be empty, and
 * nothing here pads it out.
 *
 * A metric with no `value` renders its label with a dash rather than a zero —
 * "not supplied" and "none" are different, and the Reliability principle
 * extends to the interface.
 */
export function Tile({
  label, metrics = [], motif = "none", backgroundImage, onPress, tall, tone = "dark",
  href, comingSoon, comingSoonLabel = "Coming soon", hint, ariaCurrent,
  testId = "home-tile",
}: {
  label: string;
  metrics?: TileMetric[];
  motif?: WatermarkMotif;
  backgroundImage?: string;
  onPress?: () => void;
  /** Makes the tile a real anchor — middle-click and open-in-new-tab. A tile
   *  with no module behind it gets neither this nor `onPress`. */
  href?: string;
  tall?: boolean;
  tone?: "dark" | "light" | "module";
  testId?: string;
  /**
   * A tile for a module that does not exist yet.
   *
   * Farndale has no equivalent, because every Farndale tile has a module
   * behind it — so this branch is unreachable on a Farndale render and the
   * extraction stays pixel-exact. It is here because the chassis serves apps
   * that DO have such tiles, and the product rule is that they say so IN
   * WORDS, present and plainly disabled, rather than being hidden or stubbed
   * into screens that look finished.
   *
   * The treatment is not new: it is DataState's own "unavailable" row — a
   * leading glyph, dim ink, plain words — on an otherwise unchanged tile.
   * Same card, same padding, same label size. (Drea, 2026-09-22: use the
   * closest existing primitive.)
   */
  comingSoon?: boolean;
  comingSoonLabel?: string;
  /** This is the one currently open — see `CardProps`. */
  ariaCurrent?: boolean;
  /** What the tile is FOR, so the plan is readable while it is still a plan. */
  hint?: string;
}) {
  return (
    <Card
      motif={motif}
      backgroundImage={backgroundImage}
      onPress={comingSoon ? undefined : onPress}
      href={comingSoon ? undefined : href}
      ariaLabel={comingSoon ? `${label} — ${comingSoonLabel}` : label}
      ariaDisabled={comingSoon}
      ariaCurrent={ariaCurrent}
      testId={testId}
      tone={tone}
    >
      <div className={`flex flex-col gap-3 p-5 ${tall ? "min-h-44" : "min-h-28"}`}>
        <TileLabel label={label} dim={!!comingSoon} />
        {comingSoon ? (
          <div className="flex flex-col gap-1">
            {hint && <div className="text-sm" style={{ color: "var(--nl-ink-dim)" }}>{hint}</div>}
            <div className="mt-1 flex items-center gap-2.5 text-sm" style={{ color: "var(--nl-ink-dim)" }}>
              <span aria-hidden className="text-base">⚠</span>
              <span>{comingSoonLabel}</span>
            </div>
          </div>
        ) : metrics.length > 0 && (
          <div className="flex flex-col gap-1">
            {metrics.slice(0, 3).map(m => (
              <div key={m.label} className="nl-tile__metric text-sm" data-testid="tile-metric" style={{ color: "var(--nl-ink-dim)" }}>
                <span>{m.label}: </span>
                <span
                  data-testid={m.attention && m.value !== undefined ? "tile-metric-attention" : undefined}
                  style={{
                    color: m.value === undefined
                      ? "var(--nl-ink-faint)"
                      : m.attention ? "var(--nl-danger)" : "var(--nl-ink)",
                    fontWeight: m.attention && m.value !== undefined ? 600 : undefined,
                  }}
                >
                  {m.value === undefined ? "—" : m.value}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

/**
 * A generous invisible hit area around a small visible control.
 *
 * "Don't enlarge visible elements just to create bigger touch targets (that
 * changes the approved look); use generous invisible hit areas instead."
 */
/**
 * The X that closes a screen.
 *
 * One component, because there were three copies of a dim ✕ and Drea could not
 * see any of them (2026-08-16: "make the X bigger and brighter"). Full ink
 * rather than `--nl-ink-dim`, a larger glyph, and a faint ring so it reads as
 * a control on a busy panel instead of a stray character. The hit area was
 * already 44px and stays that way — this is about being FINDABLE, which is a
 * different problem from being tappable.
 */
/**
 * A message that has to be read.
 *
 * Coloured text alone was not enough: "already on duty then: Ring Steward
 * (09:00–13:00)" was rendered in the attention colour on a dark panel and
 * Drea could not read it (2026-08-16). A refusal that cannot be read is the
 * same as a refusal with no reason — the whole point of naming the clash was
 * that the answer should be actionable.
 *
 * So a notice gets its own surface, a border, and full-strength ink.
 */
export function Notice({ children, tone = "problem" }: {
  children: ReactNode;
  tone?: "problem" | "info";
}) {
  const problem = tone === "problem";
  return (
    <div
      role={problem ? "alert" : "status"}
      className="px-3 py-2.5 text-sm"
      style={{
        borderRadius: "var(--nl-radius-sm)",
        background: problem ? "var(--nl-notice-problem-bg)" : "var(--nl-notice-info-bg)",
        border: `1px solid ${problem ? "var(--nl-notice-problem-stroke)" : "var(--nl-notice-info-stroke)"}`,
        color: "var(--nl-ink)",
      }}
    >
      {children}
    </div>
  );
}

/**
 * The stand-in for a photo nobody has uploaded.
 *
 * It was "◠", which reads as a semicircle rather than a person (Drea,
 * 2026-08-17). A head and shoulders is unambiguous at avatar size, and it
 * stays a flat outline in `currentColor` like the other icons.
 *
 * "Encourage real photos, don't require them" — the placeholder is a
 * first-class state, not a missing image, so it is worth drawing properly.
 */
export function PersonPlaceholder({ size = 28 }: { size?: number }) {
  return (
    <svg
      aria-hidden width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"
    >
      <circle cx="12" cy="8.5" r="3.75" />
      <path d="M4.5 20.5c0-4.1 3.36-7 7.5-7s7.5 2.9 7.5 7" />
    </svg>
  );
}

export function CloseButton({ onPress, label = "Close" }: { onPress?: () => void; label?: string }) {
  return (
    <HitArea label={label} onPress={onPress}>
      <span
        className="inline-flex items-center justify-center"
        style={{
          width: 34, height: 34, borderRadius: "var(--nl-pill)",
          border: "1px solid var(--nl-stroke-strong)",
          color: "var(--nl-ink)", fontSize: 20, lineHeight: 1,
        }}
      >
        ✕
      </span>
    </HitArea>
  );
}

export function HitArea({
  children, onPress, label, className = "",
}: { children: ReactNode; onPress?: () => void; label: string; className?: string }) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onPress}
      className={`relative inline-flex items-center justify-center ${className}`}
      style={{ minWidth: "var(--nl-hit-min)", minHeight: "var(--nl-hit-min)", background: "none", border: 0, padding: 0 }}
    >
      {children}
    </button>
  );
}

/** A small pill — used for group tags, filters and metric chips. */
export function Chip({
  children, onPress, selected, tone = "default",
}: {
  children: ReactNode;
  onPress?: () => void;
  selected?: boolean;
  tone?: "default" | "quiet";
}) {
  const Element = onPress ? "button" : "span";
  return (
    <Element
      type={onPress ? "button" : undefined}
      onClick={onPress}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm transition-colors"
      style={{
        borderRadius: "var(--nl-pill)",
        border: `1px solid ${selected ? "var(--nl-stroke-strong)" : "var(--nl-stroke)"}`,
        // Was --nl-tile-hover/--nl-tile (2026-09-28 fix, Drea): --nl-tile is
        // IDENTICAL to --nl-bg (#1b3a33 both) -- an unselected chip was
        // literally the same colour as the page, invisible until tapped.
        // Her own three-tier system: dark for areas/backgrounds, mid for
        // buttons, light (--nl-band) for action buttons -- a chip is a
        // button, so it belongs on the mid tier regardless of selection,
        // with selected simply brighter within that same tier.
        background: selected ? "var(--nl-pulse-tile)" : tone === "quiet" ? "transparent" : "var(--nl-add-tile)",
        color: "var(--nl-ink)",
        cursor: onPress ? "pointer" : undefined,
      }}
    >
      {children}
    </Element>
  );
}

/** A horizontally scrolling row with chevron affordances — "the same
 *  affordance as a streaming-service carousel". Shared by the pulse strip and
 *  the persistent module strip so they scroll identically. */
export function Carousel({ children, ariaLabel, align = "start" }: {
  children: ReactNode; ariaLabel: string;
  /** "stretch" makes every child the height of the tallest. */
  align?: "start" | "stretch";
}) {
  // A ref, not a render-scoped variable: the chevron handlers outlive the
  // render that created them.
  const track = useRef<HTMLDivElement | null>(null);
  const nudge = (dir: 1 | -1) => track.current?.scrollBy({ left: dir * 220, behavior: "smooth" });
  return (
    <div className="relative">
      <div
        ref={track}
        role="group"
        aria-label={ariaLabel}
        className={`nl-scroll-x flex gap-2.5 px-3 py-2 ${align === "stretch" ? "items-stretch" : ""}`}
      >
        {children}
      </div>
      <ChevronHint side="left" onPress={() => nudge(-1)} />
      <ChevronHint side="right" onPress={() => nudge(1)} />
    </div>
  );
}

function ChevronHint({ side, onPress }: { side: "left" | "right"; onPress: () => void }) {
  return (
    <button
      type="button"
      aria-label={side === "left" ? "Scroll left" : "Scroll right"}
      onClick={onPress}
      // pointer-events-none on the gradient, auto on the glyph: the hint used
      // to sit on top of the first and last tiles and swallow taps aimed at
      // them, which made the Alerts tile look dead.
      className="pointer-events-none absolute top-0 bottom-0 hidden w-7 items-center justify-center sm:flex"
      style={{
        [side]: 0,
        background: `linear-gradient(to ${side === "left" ? "right" : "left"}, var(--nl-bg-raised), transparent)`,
        color: "var(--nl-ink-faint)", border: 0, cursor: "pointer",
      } as CSSProperties}
    >
      <span className="pointer-events-auto">{side === "left" ? "‹" : "›"}</span>
    </button>
  );
}

/** Drag helper shared by the timeline handles — pointer events so it works
 *  with touch, pen and mouse without three code paths. */
export function useDragFraction(
  onFraction: (fraction: number) => void
): { onPointerDown: (e: ReactPointerEvent<HTMLElement>) => void } {
  return {
    onPointerDown(e) {
      const rail = e.currentTarget.closest("[data-nl-rail]") as HTMLElement | null;
      if (!rail) return;
      e.preventDefault();
      (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
      const move = (ev: PointerEvent) => {
        const box = rail.getBoundingClientRect();
        onFraction((ev.clientX - box.left) / box.width);
      };
      const up = () => {
        window.removeEventListener("pointermove", move);
        window.removeEventListener("pointerup", up);
      };
      window.addEventListener("pointermove", move);
      window.addEventListener("pointerup", up);
    },
  };
}
